import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, TimeoutError, from, of, throwError } from 'rxjs';
import {
  concatMap,
  delay,
  filter,
  take,
  retryWhen,
  finalize,
  catchError,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthRecoveryService } from './auth-recovery.service';
import { environment } from 'src/environments/environment';
import { TranslationService } from '../i18n/translation.service';
import { TranslateService } from '@ngx-translate/core';
import swal from 'sweetalert';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthError, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import * as Bowser from "bowser"
import { workLocalInfo } from '../shared/common/helper';

@Injectable()
export class InterceptorService implements HttpInterceptor {

  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  /**
   * @description Retry HTTP requests
   * @description Number of retry: 1
   * @description Delay: 1000ms = 1sec
   * @description Status: [408, 429, 500, 502, 503, 504]
   */
  private retry = {
    count: 1,
    delay: 1000,
    status: [204, 408, 429, 500, 502, 503, 504],
  };

  /**
   * @description Thời gian tối đa chờ MSAL về trạng thái InteractionStatus.None (ms).
   * @description Bắt buộc phải có: inProgress$ là stream vô hạn, nếu một luồng tương tác
   * @description trước đó bị kẹt (iframe treo, handleRedirectPromise không resolve) thì
   * @description trạng thái None KHÔNG BAO GIỜ phát ra và request sẽ treo vĩnh viễn.
   * @description Đặt 15s vì lúc khởi động MSAL có thể bận khá lâu (xử lý redirect hash,
   * @description gọi metadata endpoint), nhưng vượt ngưỡng này thì chắc chắn là kẹt.
   */
  private readonly msalIdleTimeoutMs = 15000;

  /**
   * @description Trạng thái tương tác hiện tại của MSAL, cập nhật liên tục từ inProgress$.
   * @description Chỉ dùng để ghi log khi timeout, giúp biết MSAL đang kẹt ở bước nào
   * @description (Startup / HandleRedirect / Login / AcquireToken / SsoSilent...).
   */
  private currentMsalInteractionStatus: InteractionStatus = InteractionStatus.None;

  /**
   * @description NHÓM 1 — Các error code BẮT BUỘC người dùng đăng nhập lại (interactive).
   * @description Silent token không bao giờ cứu được các lỗi này, retry chỉ tốn thời gian.
   * @description - no_account: chưa có account nào trong cache MSAL
   * @description - interaction_required / login_required / consent_required: AAD yêu cầu tương tác
   * @description - invalid_grant: refresh token không còn hợp lệ (hết hạn / bị thu hồi / đổi mật khẩu)
   */
  private readonly interactiveLoginErrorCodes = [
    'no_account',
    'interaction_required',
    'login_required',
    'consent_required',
    'invalid_grant',
  ];

  /**
   * @description NHÓM 1 — Các sub-error bắt buộc đăng nhập lại.
   * @description bad_token: đi kèm AADSTS700084 (refresh token của SPA có vòng đời cố định 24h,
   * @description không thể gia hạn). Lưu ý: msal-common v13 KHÔNG xếp bad_token vào
   * @description InteractionRequiredAuthError nên phải tự bắt ở đây.
   */
  private readonly interactiveLoginSubErrors = ['bad_token'];

  /**
   * @description NHÓM 1 — Các mã AADSTS chỉ xuất hiện trong error_description.
   * @description Dùng làm lưới an toàn khi MSAL không bóc tách được errorCode/subError.
   * @description - 700084: refresh token của SPA hết vòng đời cố định 24h
   * @description - 700082: refresh token hết hạn do người dùng không hoạt động lâu
   * @description - 70008 : refresh token hết hạn hoặc đã bị thu hồi
   * @description - 50058 : không có phiên đăng nhập nào để silent sign-in
   * @description - 50173 : token bị vô hiệu do đổi mật khẩu / admin thu hồi
   */
  private readonly interactiveLoginAadCodes = [
    'AADSTS700084',
    'AADSTS700082',
    'AADSTS70008',
    'AADSTS50058',
    'AADSTS50173',
  ];

  /**
   * @description NHÓM 2 — Các lỗi TẠM THỜI, phiên đăng nhập có thể vẫn còn hiệu lực.
   * @description Không được redirect ngay: chờ MSAL rảnh rồi thử silent lại 1 lần.
   * @description - interaction_in_progress: đang có luồng login/redirect khác chạy, redirect thêm sẽ làm hỏng luồng đó
   * @description - iframe_closed_prematurely / block_iframe_reload: iframe silent bị gián đoạn
   * @description - hash_empty_error / no_state_in_hash: iframe trả về response không hợp lệ
   * @description - no_network_connectivity / post_request_failed / get_request_failed: lỗi mạng
   * @description (monitor_window_timeout / monitor_popup_timeout xử lý riêng qua isMonitorTimeoutError)
   */
  private readonly transientAuthErrorCodes = [
    'interaction_in_progress',
    'iframe_closed_prematurely',
    'block_iframe_reload',
    'block_nested_iframe_reload',
    'hash_empty_error',
    'no_state_in_hash',
    'no_network_connectivity',
    'post_request_failed',
    'get_request_failed',
  ];

  /**
   * @description Lifecycle
   * @param router: Router
   * @param api: ApiService
   * @return void
   */
  constructor(
    private router: Router,
    private api: ApiService,
    private translate: TranslateService,
    private translation: TranslationService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private authMsalService: MsalService,
    private authRecovery: AuthRecoveryService
  ) {
    /**
     * Theo dõi trạng thái tương tác của MSAL để ghi vào log khi hết thời gian chờ.
     * Không unsubscribe vì interceptor sống suốt vòng đời ứng dụng.
     */
    this.msalBroadcastService.inProgress$.subscribe((status) => {
      this.currentMsalInteractionStatus = status;
    });
  }

  /**
   * @description Return true is accept
   * @description Return false do not accept
   * @param request: HttpRequest
   * @param next: HttpHandler
   * @return Observable
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isPublicRequest(request)) {
      return next.handle(request);
    }

    const activeAccount = this.authMsalService.instance.getActiveAccount();
    const account = activeAccount || this.authMsalService.instance.getAllAccounts()[0];

    // Không có account trong cache MSAL -> thuộc NHÓM 1, phải đăng nhập lại
    if (!account) {
      return this.recoverAuthentication({
        errorCode: 'no_account',
        message: 'No active MSAL account is available.'
      });
    }

    return this.acquireAndAttachToken(request, next, account).pipe(
      catchError((error) => this.handleSilentTokenError(error, request, next, account)),
      finalize(() => {
        setTimeout(() => {
          this.api.loadingOff();
        }, 300);
      })
    );
  }

  /**
   * @description Request tài nguyên tĩnh (i18n, ảnh...) thì không cần gắn token.
   * @param request: HttpRequest
   * @return boolean
   */
  private isPublicRequest(request: HttpRequest<unknown>): boolean {
    const url = request.url.toLowerCase();
    return url.startsWith('/assets/') || url.startsWith('assets/');
  }

  /**
   * @description Phục hồi phiên đăng nhập.
   * @description Uỷ quyền cho AuthRecoveryService - service dùng chung với luồng SSO callback,
   * @description để toàn ứng dụng chỉ có MỘT cờ chống trùng và MỘT bộ đếm phục hồi.
   * @param error: any
   * @return Observable
   */
  private recoverAuthentication(error: any): Observable<never> {
    return this.authRecovery.recover(error);
  }

  /**
   * @description Lỗi iframe/popup silent bị timeout.
   * @description Thường do trình duyệt chặn 3rd-party cookie hoặc tab chạy nền,
   * @description không đồng nghĩa với việc phiên đăng nhập đã hết hạn -> xếp vào NHÓM 2.
   * @param error: any
   * @return boolean
   */
  private isMonitorTimeoutError(error: any): boolean {
    return ['monitor_window_timeout', 'monitor_popup_timeout'].includes(error?.errorCode);
  }

  /**
   * @description Lấy token bằng silent flow rồi gắn vào request và gửi đi.
   * @param request: HttpRequest
   * @param next: HttpHandler
   * @param account: any
   * @return Observable
   */
  private acquireAndAttachToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    account: any,
  ): Observable<HttpEvent<unknown>> {
    return from(
      this.authMsalService.instance.acquireTokenSilent({
        scopes: ["openid", "user.read"],
        account,
        forceRefresh: false,
      })
    ).pipe(
      switchMap((response) => {
        // Một số API cũ nhận token trong body thay vì header
        const body: any = request.body;
        if (body && body['access_token']) {
          body['access_token'] = response.accessToken;
        }
        this.refreshAuthLocalStorage(response.accessToken);
        const authRequest = this.addToken(request, response.accessToken);
        return next.handle(authRequest).pipe(
          tap((event) => {
            if (event instanceof HttpResponse) {
              // Request thành công nghĩa là token dùng được -> xoá bộ đếm phục hồi
              this.authRecovery.resetRecoverAttempts();
            }
          }),
          retryWhen((error) =>
            error.pipe(
              concatMap((err, count) => this.retryContent(err, count)),
              delay(this.retry.delay)
            )
          )
        );
      })
    );
  }

  /**
   * @description Điều phối lỗi khi acquireTokenSilent thất bại lần đầu.
   * @description NHÓM 1 -> redirect login ngay.
   * @description NHÓM 2 + UNKNOWN -> chờ MSAL rảnh rồi thử silent lại 1 lần.
   * @param error: any
   * @param request: HttpRequest
   * @param next: HttpHandler
   * @param account: any
   * @return Observable
   */
  private handleSilentTokenError(
    error: any,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    account: any,
  ): Observable<HttpEvent<unknown>> {
    const classification = this.classifyAuthError(error);

    console.warn('[MSAL] Silent token acquisition failed.', {
      classification,
      errorCode: error?.errorCode,
      subError: error?.subError,
      name: error?.name,
      message: error?.message,
      willRetryAfterIdle: classification !== 'INTERACTIVE_LOGIN',
    });

    // NHÓM 1: retry cũng vô ích, đưa người dùng về trang đăng nhập luôn
    if (classification === 'INTERACTIVE_LOGIN') {
      return this.recoverAuthentication(error);
    }

    // NHÓM 2 và lỗi chưa rõ: cho MSAL thêm 1 cơ hội silent trước khi kết luận
    return this.waitForMsalIdleAndRetry(request, next, account);
  }

  /**
   * @description NHÓM 2: chờ MSAL xử lý xong luồng đang chạy (InteractionStatus.None)
   * @description rồi thử lấy token silent thêm ĐÚNG 1 lần.
   * @description Chờ như vậy để tránh lỗi interaction_in_progress do gọi chồng lên nhau.
   * @description Nếu lần thử này vẫn fail thì chuyển sang chốt chặn cuối handleFinalTokenError().
   * @param request: HttpRequest
   * @param next: HttpHandler
   * @param account: any
   * @return Observable
   */
  private waitForMsalIdleAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    account: any,
  ): Observable<HttpEvent<unknown>> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
      /**
       * Chặn treo vĩnh viễn: inProgress$ không bao giờ complete, nên nếu MSAL kẹt ở
       * một trạng thái khác None thì filter() ở trên sẽ không bao giờ phát giá trị.
       * timeout() đặt SAU take(1) nên chỉ tính thời gian chờ trạng thái None,
       * không ảnh hưởng tới thời gian chạy của request phía dưới switchMap.
       */
      timeout(this.msalIdleTimeoutMs),
      switchMap(() => {
        console.info('[MSAL] InteractionStatus.None reached. Retrying silent token acquisition once.');
        return this.acquireAndAttachToken(request, next, account);
      }),
      catchError((retryError) => {
        // Hết thời gian chờ: MSAL kẹt, không thể phục hồi bằng silent -> bắt đăng nhập lại
        if (this.isTimeoutError(retryError)) {
          console.error('[MSAL] Timed out waiting for InteractionStatus.None.', {
            timeoutMs: this.msalIdleTimeoutMs,
            currentStatus: this.currentMsalInteractionStatus,
          });
          return this.recoverAuthentication({
            errorCode: 'msal_idle_timeout',
            message: `MSAL did not reach InteractionStatus.None within ${this.msalIdleTimeoutMs}ms.`,
          });
        }
        return this.handleFinalTokenError(retryError, account);
      })
    );
  }

  /**
   * @description Nhận diện lỗi timeout của RxJS.
   * @description Kiểm tra cả instanceof lẫn name vì TimeoutError có thể đi qua nhiều
   * @description bản RxJS khác nhau khiến instanceof không đáng tin cậy.
   * @param error: any
   * @return boolean
   */
  private isTimeoutError(error: any): boolean {
    return error instanceof TimeoutError || error?.name === 'TimeoutError';
  }

  /**
   * @description Chốt chặn cuối cùng: chạy sau khi đã thử silent lại mà vẫn fail.
   * @description Tách 2 loại lỗi:
   * @description - HttpErrorResponse: token lấy được rồi, chính request API mới là cái fail
   * @description - Còn lại: lỗi xác thực -> NHÓM 3, không phân loại thêm nữa mà bắt đăng nhập lại
   * @param error: any
   * @param activeAccount: any
   * @return Observable
   */
  private handleFinalTokenError(error: any, activeAccount: any): Observable<never> {
    this.api.loadingOff();
    this.api.loadingCustomOff();

    // Lỗi trả về từ API nghiệp vụ
    if (error instanceof HttpErrorResponse) {
      return this.handleHttpError(error, activeAccount);
    }

    /**
     * NHÓM 3: mọi lỗi xác thực còn lại.
     * Đã thử silent 2 lần đều fail thì coi như phiên không thể phục hồi, dù error code
     * là gì đi nữa. Nhánh này bịt luôn các error code lạ/mới của MSAL trong tương lai,
     * thay cho việc throwError trống làm người dùng bị treo màn hình.
     */
    if (this.isAuthRelatedError(error)) {
      console.error('[MSAL] Auth error is unrecoverable after retry. Forcing interactive login.', {
        classification: this.classifyAuthError(error),
        errorCode: error?.errorCode,
        subError: error?.subError,
        message: error?.message,
      });
      return this.recoverAuthentication(error);
    }

    // Lỗi runtime không liên quan xác thực: ném lại cho component tự xử lý
    console.error('[Interceptor] Non-auth error while handling request.', error);
    return throwError(error);
  }

  /**
   * @description Xử lý lỗi HTTP trả về từ API nghiệp vụ.
   * @param error: HttpErrorResponse
   * @param activeAccount: any
   * @return Observable
   */
  private handleHttpError(error: HttpErrorResponse, activeAccount: any): Observable<never> {
    // 401: token không được backend chấp nhận -> đăng xuất hẳn
    if (error.status === 401) {
      swal({
        icon: 'error',
        title: this.translate.instant('COMMON.msg.expires'),
        buttons: { ok: this.translate.instant('COMMON.btn.close') },
      } as any).then(() => {
        this.api.logout(this.authMsalService, activeAccount);
      });
      return throwError(error.status);
    }

    // 403: không đủ quyền -> chỉ báo lỗi, giữ nguyên phiên đăng nhập
    if (error.status === 403) {
      swal({
        icon: 'error',
        title: error.error ?? 'Đã có lỗi xảy ra :(',
        buttons: { ok: this.translate.instant('COMMON.btn.close') },
      } as any);
      return throwError(error);
    }

    /**
     * Còn lại: 5xx sẽ được showErrorPage() điều hướng sang trang lỗi.
     * Các mã khác (0, 400, 404...) cố tình không hiện popup chung vì đây là lỗi
     * nghiệp vụ, để từng component tự hiển thị thông báo phù hợp với ngữ cảnh.
     */
    this.showErrorPage(error);
    return throwError(error);
  }

  /**
   * @description Add header authorization
   * @param request: HttpRequest
   * @param token: string
   * @return HttpRequest
   */
  private addToken(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<unknown> {
    const workInfo = this.getWorkLocalInfo()
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
        'language': this.translation.getSelectedLanguage(),
        'Agent': workInfo.workEmail,
        'OS-version': this.getBrowserInfo(),
        'Platform': 'Web',
        'Department': workInfo.departmentCode
      }
    });
  }

  /**
   * @description Retry content
   * @param error: any
   * @param count: number
   * @return Observable
   */
  private retryContent(error: any, count: number): Observable<any> {
    if (count < this.retry.count && this.retry.status.includes(error?.status)) {
      return of(error);
    }
    return throwError(error);
  }

  private getAuthFromLocal() {
    try {
      const authRaw = localStorage.getItem(this.authLocalStorageToken);
      const authData = authRaw ? JSON.parse(authRaw) : undefined;
      return authData;
    } catch (error) {
      return undefined;
    }
  }

  private refreshAuthLocalStorage(newToken: string) {
    const authFromLocal = this.getAuthFromLocal();
    const newAuth = {
      ...authFromLocal,
      accessToken: newToken
    }
    localStorage.setItem(this.authLocalStorageToken, JSON.stringify(newAuth))
  }

  /**
   * @description Show error page
   * @param error: HttpErrorResponse
   * @return void
   */
  private showErrorPage(error: HttpErrorResponse): void {
    // List of unwanted system errors
    // 500: Internal Server Error
    // 501: Not Implemented
    // 502: Bad Gateway
    // 503: Service Unavailable
    // 504: Gateway Timeout
    // 505: HTTP Version Not Supported
    const status = [500, 501, 502, 503, 504, 505];
    // Check does exist
    const isShowError = status.includes(error?.status);
    if (isShowError) {
      // Redirect to error page
      this.router.navigate(['/', 'error', 'server', error?.status], {
        queryParams: { status: error?.status },
      });
    }
  }

  private getBrowserInfo(){
    const browser = Bowser.getParser(window.navigator.userAgent);
    const browserObject = browser.getBrowser();
    return browserObject.name + " " + browserObject.version
  }

  private getWorkLocalInfo(){
    const workLocalRaw = localStorage.getItem(workLocalInfo)
    const workLocalObject = workLocalRaw ? JSON.parse(workLocalRaw) : undefined
    return {
      workEmail: workLocalObject?.workEmail ?? '', 
      departmentCode: workLocalObject?.departmentCode ?? ''
    }
  }

  /**
   * @description NHÓM 1: Lỗi bắt buộc người dùng đăng nhập lại (interactive).
   * @description Gặp nhóm này thì redirect NGAY, không retry silent vì chắc chắn fail.
   * @param error: any
   * @return boolean
   */
  private requiresInteractiveLogin(error: any): boolean {
    // MSAL đã tự phân loại: chắc chắn cần tương tác người dùng
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    // Bắt bằng errorCode (vd: invalid_grant khi refresh token chết)
    if (this.interactiveLoginErrorCodes.includes(error?.errorCode)) {
      return true;
    }
    // Bắt bằng subError (vd: bad_token - msal-common v13 không xếp vào InteractionRequiredAuthError)
    if (this.interactiveLoginSubErrors.includes(error?.subError)) {
      return true;
    }
    // Lưới an toàn: mã AADSTS chỉ nằm trong error_description
    return this.hasInteractiveLoginAadCode(error);
  }

  /**
   * @description Dò mã AADSTS trong message/error_description của lỗi.
   * @description Cần thiết vì MSAL không phải lúc nào cũng bóc tách được errorCode/subError.
   * @param error: any
   * @return boolean
   */
  private hasInteractiveLoginAadCode(error: any): boolean {
    const message = `${error?.errorMessage ?? ''} ${error?.message ?? ''}`;
    return this.interactiveLoginAadCodes.some((code) => message.includes(code));
  }

  /**
   * @description NHÓM 2: Lỗi tạm thời, phiên đăng nhập có thể vẫn còn hiệu lực.
   * @description Gặp nhóm này thì chờ MSAL rảnh rồi thử silent lại, KHÔNG redirect ngay.
   * @param error: any
   * @return boolean
   */
  private isTransientAuthError(error: any): boolean {
    // iframe silent bị chặn (3rd-party cookie) hoặc timeout - session AAD có thể vẫn còn nguyên
    if (this.isMonitorTimeoutError(error)) {
      return true;
    }
    return this.transientAuthErrorCodes.includes(error?.errorCode);
  }

  /**
   * @description Kiểm tra lỗi có phải do MSAL/xác thực sinh ra hay không.
   * @description Dùng để tránh việc một lỗi runtime bất kỳ (TypeError...) cũng kích hoạt redirect login.
   * @param error: any
   * @return boolean
   */
  private isAuthRelatedError(error: any): boolean {
    return error instanceof AuthError || !!error?.errorCode;
  }

  /**
   * @description Gắn nhãn nhóm lỗi để ghi log, giúp debug nhanh trên production.
   * @param error: any
   * @return string
   */
  private classifyAuthError(error: any): string {
    if (this.requiresInteractiveLogin(error)) {
      return 'INTERACTIVE_LOGIN';
    }
    if (this.isTransientAuthError(error)) {
      return 'TRANSIENT';
    }
    return 'UNKNOWN';
  }
}
